import {
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  FileCopy as CopyIcon
} from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { Image } from "components/Image";
import {
  formatTimeRemaining,
  isExpiredCreative,
  MAX_EDIT_COUNT
} from "./creativeHelpers";
import type { GeneratedUserCreative } from "../../../@types/sharedSchema";

export type CreativeCardProps = {
  creative: GeneratedUserCreative;
  countdownSeconds?: number;
  isAdminUser?: boolean;
  onView: (imageUrl: string, creative: GeneratedUserCreative) => void;
  onEdit: (creative: GeneratedUserCreative) => void;
  onDownload: (imageUrl: string) => void;
  onCopyPrompt?: (creative: GeneratedUserCreative) => void;
};

export function CreativeCard({
  creative,
  countdownSeconds,
  isAdminUser,
  onView,
  onEdit,
  onDownload,
  onCopyPrompt
}: CreativeCardProps): JSX.Element {
  const { t } = useTranslation();

  const isCompleted = creative.status === "completed";
  const isPending = creative.status === "starting" || creative.status === "in-progress";
  const isFailed = creative.status === "failed" || isExpiredCreative(creative);
  const editLimitReached = (creative.editCount ?? 0) >= MAX_EDIT_COUNT;

  return (
    <Card>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          paddingTop: "100%",
          cursor: isCompleted ? "pointer" : "default"
        }}
        onClick={() => {
          if (isCompleted) onView(creative.generatedCreativeUrl, creative);
        }}
      >
        {creative.generatedCreativeUrl && (
          <Image
            src={creative.generatedCreativeUrl}
            alt="Generated Creative"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                transform: isCompleted ? "scale(1.02)" : "none"
              },
              opacity: creative.status === "failed" ? 0.5 : 1
            }}
          />
        )}

        {/* Edit countdown chip - Top left overlay */}
        {isCompleted &&
          creative.createdTime &&
          countdownSeconds !== undefined &&
          countdownSeconds > 0 && (
            <Box sx={{ position: "absolute", top: 8, left: 8, zIndex: 1 }}>
              <Chip
                label={t(
                  "creativesGenerator.editTimeRemaining",
                  "עריכה חינם: {{time}}",
                  { time: formatTimeRemaining(countdownSeconds) }
                )}
                size="small"
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  color: "text.primary",
                  fontSize: "0.7rem",
                  height: "auto",
                  "& .MuiChip-label": { px: 1, py: 0.5 }
                }}
              />
            </Box>
          )}

        {/* Status overlay for pending creatives */}
        {isPending && !isExpiredCreative(creative) && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "white"
            }}
          >
            <CircularProgress size={40} sx={{ color: "white", mb: 1 }} />
            <Typography variant="body2" sx={{ textAlign: "center" }}>
              {t("creativesGenerator.generating", "יוצר קריאטיב...")}
            </Typography>
          </Box>
        )}

        {/* Status overlay for failed or expired creatives */}
        {isFailed && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(244, 67, 54, 0.8)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "white"
            }}
          >
            <ErrorIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2" sx={{ textAlign: "center" }}>
              {t("creativesGenerator.failed", "יצירה נכשלה")}
            </Typography>
            {(creative.errorMessage || isExpiredCreative(creative)) && (
              <Typography
                variant="body2"
                sx={{ textAlign: "center", fontSize: 10, mt: 1, mx: 2 }}
              >
                {isExpiredCreative(creative)
                  ? t(
                      "creativesGenerator.expired",
                      "אירעה שגיאה והקריאטיב סומן כפג תוקף"
                    )
                  : creative.errorMessage!.length > 150
                  ? creative.errorMessage!.slice(0, 150) + "..."
                  : creative.errorMessage}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <CardContent sx={{ pb: 1 }}>
        <Typography variant="body2" color="textSecondary">
          {creative.createdTime?.toLocaleDateString("he-IL", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
          })}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: "center", pt: 0 }}>
        <IconButton
          size="small"
          onClick={() => onView(creative.generatedCreativeUrl, creative)}
          title={t("creativesGenerator.view", "צפייה")}
          disabled={!isCompleted}
        >
          <ViewIcon />
        </IconButton>
        <Tooltip
          title={
            editLimitReached
              ? t(
                  "creativesGenerator.editLimitTooltip",
                  "ניתן לערוך קריאטיב עד {{max}} פעמים בלבד, על מנת לשמור על איכות הקריאטיב",
                  { max: MAX_EDIT_COUNT }
                )
              : t("creativesGenerator.edit", "עריכה")
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={() => onEdit(creative)}
              color="primary"
              disabled={!isCompleted || editLimitReached}
            >
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          size="small"
          onClick={() => onDownload(creative.generatedCreativeUrl)}
          title={t("creativesGenerator.download", "הורדה")}
          disabled={!isCompleted}
        >
          <DownloadIcon />
        </IconButton>
        {isAdminUser && onCopyPrompt && (
          <IconButton
            size="small"
            onClick={() => onCopyPrompt(creative)}
            title="Copy Prompt"
            disabled={!isCompleted || !creative.prompt}
          >
            <CopyIcon />
          </IconButton>
        )}
      </CardActions>
    </Card>
  );
}
